import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, shareReplay, finalize } from 'rxjs';
import { environment } from '../../environments/environment';
import { PromoCode, PromoCodeUsage, UserLicense } from '../models/models';

export interface LicenseStatusResponse {
  success: boolean;
  active_license: UserLicense | null;
  licenses: UserLicense[];
}

export interface RedeemResponse {
  success: boolean;
  license: UserLicense;
}

@Injectable({
  providedIn: 'root'
})
export class LicenseManagementService {
  private apiUrl = environment.apiUrl;
  private licenseStatusRequest$: Observable<LicenseStatusResponse> | null = null;

  constructor(private http: HttpClient) {}

  // Endnutzer: Promo-Code einlösen (optional mit Laufzeit in Monaten, default 12)
  redeemPromoCode(code: string, durationMonths?: number): Observable<RedeemResponse> {
    return this.http.post<RedeemResponse>(`${this.apiUrl}/licenses/redeem`, {
      promo_code: code,
      duration_months: durationMonths
    });
  }

  // Endnutzer: Bestehenden Lizenzschlüssel aktivieren/binden
  activateLicenseKey(licenseKey: string): Observable<RedeemResponse> {
    return this.http.post<RedeemResponse>(`${this.apiUrl}/licenses/activate`, {
      license_key: licenseKey
    });
  }

  // Endnutzer: Lizenzstatus abfragen
  getLicenseStatus(): Observable<LicenseStatusResponse> {
    return this.http.get<LicenseStatusResponse>(`${this.apiUrl}/licenses/status`);
  }

  // Lizenzstatus zwischenspeichern, um doppelte parallele Requests zu vermeiden
  getLicenseStatusCached(forceRefresh = false): Observable<LicenseStatusResponse> {
    if (!forceRefresh && this.licenseStatusRequest$) {
      return this.licenseStatusRequest$;
    }

    this.licenseStatusRequest$ = this.getLicenseStatus().pipe(
      finalize(() => {
        this.licenseStatusRequest$ = null;
      }),
      shareReplay(1)
    );

    return this.licenseStatusRequest$;
  }

  // Endnutzer: Lizenz anfragen (Mail an Admins)
  requestLicense(): Observable<{ success: boolean; message: string }> {
    return this.http.post<{ success: boolean; message: string }>(`${this.apiUrl}/licenses/request`, {});
  }

  // Admin: Promo-Code-Liste
  getPromoCodes(): Observable<{ promo_codes: PromoCode[] }> {
    return this.http.get<{ promo_codes: PromoCode[] }>(`${this.apiUrl}/promo-codes/index.php`);
  }

  // Admin: Einzelner Promo-Code inkl. Nutzungen
  getPromoCode(id: number): Observable<{ promo_code: PromoCode; usages: PromoCodeUsage[] }> {
    return this.http.get<{ promo_code: PromoCode; usages: PromoCodeUsage[] }>(`${this.apiUrl}/promo-codes/index.php?id=${id}`);
  }

  // Admin: Promo-Code erstellen
  createPromoCode(payload: Partial<PromoCode>): Observable<{ success: boolean; id: number }> {
    return this.http.post<{ success: boolean; id: number }>(`${this.apiUrl}/promo-codes/index.php`, payload);
  }

  // Admin: Promo-Code aktualisieren
  updatePromoCode(id: number, payload: Partial<PromoCode>): Observable<{ success: boolean }> {
    return this.http.put<{ success: boolean }>(`${this.apiUrl}/promo-codes/index.php?id=${id}`, payload);
  }

  // Admin: Promo-Code löschen
  deletePromoCode(id: number): Observable<{ success: boolean }> {
    return this.http.delete<{ success: boolean }>(`${this.apiUrl}/promo-codes/index.php?id=${id}`);
  }

  // Admin: Promo-Code f端r bestimmten Nutzer einl枚sen
  adminRedeemPromoCode(userId: number, code: string, durationMonths?: number): Observable<RedeemResponse> {
    return this.http.post<RedeemResponse>(`${this.apiUrl}/admin/redeem-promo`, {
      user_id: userId,
      promo_code: code,
      duration_months: durationMonths
    });
  }
}
